/**
 * Shared API-mutation error handling — persona-audit Loop 1 (MAJOR).
 *
 * Before this, every hand-rolled `fetch(...).then(r => { if (!r.ok) throw new
 * Error('Failed to create X') })` threw a plain Error with no status, so a
 * mutation error handler could never tell "the server rejected this because
 * you don't have permission" (403, capability gap) from "the request was
 * malformed" or "the server is down". The worst instance (GatePage's Declare
 * button) failed COMPLETELY SILENTLY for a VIEWER — the button was enabled,
 * the click did nothing visible, and only a network tab would show the 403.
 *
 * `ApiError` carries the HTTP status; `throwIfNotOk` is a drop-in replacement
 * for the `if (!response.ok) throw new Error(...)` idiom used throughout
 * `hooks/use*.ts`; `showMutationErrorToast` is the one place that decides
 * "permission" vs "something else failed" copy, so every call site gets the
 * same honest message instead of reinventing (or omitting) one.
 */
import { showToast } from '../components/Notifications/Toast';

export class ApiError extends Error {
  status: number;
  /** The backend's `ServiceError.code` when present (e.g. 'VALIDATION_ERROR',
   * 'CONFLICT', 'VERSION_CONFLICT' — see errorHandler.ts's `classifyError`).
   * Lets callers distinguish error *kinds* that share an HTTP status, e.g. a
   * 400 field-validation failure vs. a 400 stale-id-reference. */
  code?: string;

  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/**
 * Friendly stand-in for a server-side failure (persona-audit i3, MINOR). A 5xx
 * means the server broke, not the user — the raw backend text (e.g. "Internal
 * Server Error (forced by audit)", stack line/method info) is noise at best and
 * an information leak at worst, so it never reaches the user toast/console.
 * 4xx messages (Zod field errors, 403 permission copy) are ACTIONABLE and are
 * still surfaced verbatim.
 */
export const GENERIC_SERVER_ERROR_MESSAGE = 'Something went wrong — please try again.';

/**
 * Friendly copy for the "you interacted with an entity that was deleted in
 * another tab" case (persona-audit i3, MINOR). Mirrors the clean message the
 * reload path already shows, instead of a raw "…: Bad Request".
 */
export const STALE_ENTITY_MESSAGE =
  'This record was deleted or can no longer be found. Reload the page to continue.';

/**
 * Fallback copy for a 409 optimistic-lock conflict (persona-audit L1, MAJOR)
 * when the server didn't hand us a more specific message. Distinct from
 * `STALE_ENTITY_MESSAGE`: a conflict means the record still exists and was
 * merely edited by someone else, not deleted — telling the user to "reload,
 * it's gone" is actively wrong and sends them looking for a record that's
 * still there.
 */
export const CONFLICT_MESSAGE =
  'This was changed by someone else while you were editing. Reload to get the latest, then re-apply your change.';

/** True for any error carrying a 5xx `.status` (ApiError or a duck-typed peer). */
export function isServerError(error: unknown): boolean {
  const status = getErrorStatus(error);
  return typeof status === 'number' && status >= 500;
}

/**
 * True for the "stale / deleted-in-another-tab" interaction case: a 404 (the
 * parent entity is gone) or a 400 (the request references an id the server no
 * longer recognises). Used to swap a raw downstream error for
 * `STALE_ENTITY_MESSAGE`.
 *
 * EXCLUDES a 400 whose `code` is `'VALIDATION_ERROR'` (persona-audit L1,
 * MAJOR) — that's a `ValidationError` (business-rule/field failure, e.g.
 * mitigation.service.ts's "Invalid status transition from PLANNED to
 * VERIFIED…") which carries a specific, actionable message. Swapping it for
 * "This record was deleted or can no longer be found" would be actively
 * wrong (the record is right there, the EDIT was rejected) on top of hiding
 * the one piece of information — which transitions ARE allowed — the user
 * needs to fix their input. Matches `throwIfNotOk`'s own doctrine above:
 * "4xx field messages... are still surfaced verbatim because they're
 * actionable".
 */
export function isStaleEntityError(error: unknown): boolean {
  const status = getErrorStatus(error);
  if (status === 404) return true;
  if (status !== 400) return false;
  return getErrorCode(error) !== 'VALIDATION_ERROR';
}

/**
 * True for a 409 optimistic-lock conflict — `ConflictError`/`VERSION_CONFLICT`
 * on the backend (note.service.ts, mitigation.service.ts, requirement.service.ts,
 * etc. all throw this shape; see errorHandler.ts's `classifyError`, which
 * serialises it as `{ error, code, details }` with `code: 'CONFLICT' |
 * 'VERSION_CONFLICT'`). Checked on `.status` first (the common case for every
 * fetch-wrapper error class in this app — ApiError, NotesApiError,
 * InterlockApiError all carry it), falling back to a duck-typed `.code` /
 * `.body.code` read for callers that only surface the parsed body.
 *
 * Distinct from `isStaleEntityError` (404/400 — the record is GONE): a 409
 * means the record still exists but was edited by someone else since this
 * client loaded it. Conflating the two (as the note-edit and
 * verification-record-edit save paths used to, hand-rolling their own
 * `status === 409` check straight to `STALE_ENTITY_MESSAGE`) told the user
 * their in-progress edit target had been deleted when it hadn't.
 */
export function isConflictError(error: unknown): boolean {
  const status = getErrorStatus(error);
  if (status === 409) return true;
  const code = getErrorCode(error);
  return code === 'CONFLICT' || code === 'VERSION_CONFLICT';
}

/** Duck-typed `.status` reader shared by the helpers above. */
function getErrorStatus(error: unknown): number | undefined {
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const status = (error as { status?: unknown }).status;
    return typeof status === 'number' ? status : undefined;
  }
  return undefined;
}

/** Duck-typed `.code` / `.body.code` reader for {@link isConflictError}. */
function getErrorCode(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const e = error as { code?: unknown; body?: { code?: unknown } };
  if (typeof e.code === 'string') return e.code;
  if (typeof e.body?.code === 'string') return e.body.code;
  return undefined;
}

/** Drop-in replacement for `if (!response.ok) throw new Error(fallbackMessage)`. */
export async function throwIfNotOk(response: Response, fallbackMessage: string): Promise<void> {
  if (response.ok) return;
  // 5xx (persona-audit i3, MINOR): the server broke — never surface its raw
  // error text (stack/method info, "…(forced by audit)") to the user. Throw a
  // status-carrying ApiError with generic copy; 4xx field messages below are
  // still surfaced verbatim because they're actionable.
  if (response.status >= 500) {
    throw new ApiError(response.status, GENERIC_SERVER_ERROR_MESSAGE);
  }
  // Best-effort: prefer a server-provided message if the body is JSON with one,
  // otherwise fall back to the caller's generic copy.
  let serverMessage: string | undefined;
  let serverCode: string | undefined;
  try {
    const body = await response.clone().json();
    // Zod validation failures (validateZodRequest / formatZodValidationError)
    // return `{ error: 'Validation failed', details: [{ field, message }] }`.
    // A bare "Validation failed" toast tells the user nothing — surface the
    // per-field messages (e.g. "title: String must contain at most 500
    // character(s)") so they know WHICH field and WHAT limit. Same idea as the
    // interface-create form's field-error surfacing.
    const details = Array.isArray(body?.details) ? body.details : undefined;
    const fieldMessages = details
      ?.map((d: { field?: string; path?: string; message?: string }) => {
        const field = d?.field ?? d?.path;
        if (typeof d?.message !== 'string') return undefined;
        return field && field !== 'unknown' ? `${field}: ${d.message}` : d.message;
      })
      .filter((m: unknown): m is string => typeof m === 'string' && m.length > 0);
    serverMessage =
      (fieldMessages && fieldMessages.length > 0 ? fieldMessages.join('; ') : undefined) ||
      (typeof body?.error === 'string' && body.error) ||
      (typeof body?.message === 'string' && body.message) ||
      undefined;
    // `code` (ServiceError.code, e.g. 'VALIDATION_ERROR' / 'CONFLICT' /
    // 'VERSION_CONFLICT') rides along so isConflictError/isStaleEntityError
    // can tell error *kinds* apart within the same HTTP status.
    serverCode = typeof body?.code === 'string' ? body.code : undefined;
  } catch {
    /* not JSON / empty body — fine, use fallbackMessage */
  }
  throw new ApiError(response.status, serverMessage || fallbackMessage, serverCode);
}

/**
 * True for a 403 from EITHER this module's `ApiError` (the plain-fetch hooks
 * under `hooks/use*.ts`) or `interlockApi.ts`'s own `InterlockApiError`
 * (GatePage, RiskRegisterPage, etc. — which already carries `.status` via its
 * `requestJson`/`getJson` helpers, see `isForbidden()` there). Duck-typed on
 * `.status` rather than importing `InterlockApiError` to avoid a dependency
 * back onto the (much larger) interlockApi.ts module from this small utility.
 */
export function isForbiddenError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    (error as { status?: unknown }).status === 403
  );
}

/**
 * The one shared "a mutation failed" toast. 403 (the server's authz
 * boundary — requireCapability() middleware) ALWAYS reads as a permission
 * problem, regardless of what the server's own error string says, so a
 * VIEWER/MANAGER clicking a CTA that should have been gated gets an honest
 * "you can't do this" instead of a confusing generic failure message.
 */
export function showMutationErrorToast(error: unknown, fallbackMessage: string): void {
  if (isForbiddenError(error)) {
    showToast.error("You don't have permission to do this.");
    return;
  }
  // Optimistic-lock conflict (409 — the record still exists but someone else
  // saved a change to it since this client loaded it): checked BEFORE
  // isStaleEntityError so a conflict never gets mislabelled as a deletion.
  // Prefer the backend's own message when it gave us one (e.g.
  // note.service.ts's "Version conflict: this note was modified by someone
  // else. Reload and retry." — already specific and user-friendly) over the
  // generic fallback, same "server message wins when present" idiom
  // `throwIfNotOk` already uses for field-validation errors above.
  if (isConflictError(error)) {
    const message = error instanceof Error && error.message ? error.message : CONFLICT_MESSAGE;
    showToast.error(message);
    return;
  }
  // Stale/deleted entity (404/400 — the record this mutation targeted is gone
  // or the id it referenced no longer resolves): never echo the raw backend
  // text, which can leak internal ids (e.g. "Requirement with id cmrs8... not
  // found"). Show the same friendly copy the reload path already uses.
  if (isStaleEntityError(error)) {
    showToast.error(STALE_ENTITY_MESSAGE);
    return;
  }
  // 5xx (persona-audit i3, MINOR): never echo the raw backend error text — even
  // if an error carrying a 500 status slipped through with a server message
  // (e.g. InterlockApiError), the user gets friendly generic copy instead.
  if (isServerError(error)) {
    showToast.error(GENERIC_SERVER_ERROR_MESSAGE);
    return;
  }
  const message = error instanceof Error ? error.message : fallbackMessage;
  showToast.error(message || fallbackMessage);
}
